import { useId } from 'react'
import { CheckCircle2, Plus, Trash2, Zap } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AppraisalField } from '@/components/deals/appraisal/AppraisalField'
import {
  INITIAL_COMMERCIAL_UNITS,
  commercialUnitOpexDefault,
  isCommercialUseType,
} from '@/components/deals/appraisal/appraisal-data'
import {
  calculateCommercialSummary,
  calculateCommercialUnit,
  formatNumber,
} from '@/components/deals/appraisal/appraisal-calculations'
import { marketInputGuidance } from '@/components/deals/appraisal/appraisal-market-guidance'
import { cn } from '@/lib/utils'

const DEFAULT_STATE = {
  units: INITIAL_COMMERCIAL_UNITS,
}

const USE_TYPES = [
  'Retail',
  'Office',
  'Industrial / Warehouse',
  '1-Bed Flat',
  '2-Bed Flat',
  '3-Bed Flat',
  '2-Bed Terraced House',
  '3-Bed Terraced House',
  '2-Bed Semi-Detached House',
  '3-Bed Semi-Detached House',
  '4-Bed Detached House',
  'Flat',
  'Studio',
  'Other Commercial',
  'Other Residential',
]
const RESIDENTIAL_OPERATING_MODELS = ['AST', 'SA']
const COMMERCIAL_VALUATION_BASES = ['Comparable', '£/sqft', 'Commercial']

export function CommercialMixedUseScheme({
  state,
  postcodeArea,
  showValidationErrors,
  isCoWork = false,
  onStateChange,
}) {
  const schemeState = { ...DEFAULT_STATE, ...state }
  const units = schemeState.units?.length
    ? schemeState.units
    : INITIAL_COMMERCIAL_UNITS.map((unit) => ({
      ...unit,
      opexPercent: commercialUnitOpexDefault({
        useType: unit.useType,
        operatingModel: unit.operatingModel,
        isCoWork,
      }),
    }))
  const summary = calculateCommercialSummary(units)
  const combinedValue = summary.commercial + summary.residential

  function updateState(patch) {
    onStateChange?.({ ...schemeState, units, ...patch })
  }

  function updateUnit(unitId, field, value) {
    updateState({
      units: units.map((unit) => {
        if (unit.id !== unitId) return unit

        const previousDefault = commercialUnitOpexDefault({
          useType: unit.useType,
          operatingModel: unit.operatingModel,
          isCoWork,
        })
        const updated = { ...unit, [field]: value }
        if (field === 'useType') {
          if (isCommercialUseType(value)) {
            updated.valuationBasis = 'Commercial'
          } else {
            updated.operatingModel = 'AST'
            updated.valuationBasis = 'Comparable'
          }
        }
        if (
          ['operatingModel', 'useType'].includes(field) &&
          (unit.opexPercent == null || String(unit.opexPercent) === previousDefault)
        ) {
          updated.opexPercent = commercialUnitOpexDefault({
            useType: updated.useType,
            operatingModel: updated.operatingModel,
            isCoWork,
          })
        }

        return updated
      }),
    })
  }

  function addUnit() {
    const nextId = Math.max(...units.map((unit) => Number(unit.id) || 0), 0) + 1
    updateState({
      units: [
        ...units,
        {
          id: nextId,
          name: `Unit ${units.length + 1}`,
          useType: 'Retail',
          sizeSqft: '1,000',
          operatingModel: 'AST',
          valuationBasis: 'Commercial',
          suggestedSqftYear: '',
          adoptedSqftYear: '',
          opexPercent: commercialUnitOpexDefault({
            useType: 'Retail',
            operatingModel: 'AST',
            isCoWork,
          }),
          adoptedYield: '8',
          suggestedMonthlyRent: '',
          adoptedMonthlyRent: '',
          suggestedAdr: '',
          adoptedAdr: '',
          occupancy: '75',
          suggestedValue: '',
          adoptedValue: '',
          suggestedSqftValue: '',
          adoptedSqftValue: '',
        },
      ],
    })
  }

  function removeUnit(unitId) {
    updateState({ units: units.filter((unit) => unit.id !== unitId) })
  }

  function marketGuidance(suggestedValue, adoptedValue) {
    return marketInputGuidance({
      suggestedValue,
      adoptedValue,
      postcodeArea,
      showValidationErrors,
    })
  }

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-foreground">Unit Schedule</h3>
          <Button
            type="button"
            size="sm"
            onClick={addUnit}
            className="gap-1.5 bg-primary/10 text-primary shadow-none hover:bg-primary/20 hover:text-primary"
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            Add Unit
          </Button>
        </div>

        <div className="space-y-3">
          {units.map((unit, index) => (
            <UnitCard
              key={unit.id}
              unit={unit}
              rowLabel={`Unit ${index + 1}`}
              canRemove={units.length > 1}
              marketGuidance={marketGuidance}
              onUpdate={(field, value) => updateUnit(unit.id, field, value)}
              onRemove={() => removeUnit(unit.id)}
            />
          ))}
        </div>
      </section>

      <section className="border-t border-border/50 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-5 rounded-xl border border-primary/20 bg-primary/5 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/20">
              <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground">Scheme Summary</h4>
              <p className="text-xs font-medium text-muted-foreground">
                Adopted values driving the appraisal
              </p>
            </div>
          </div>
          <dl className="grid w-full gap-x-12 gap-y-6 text-right sm:w-auto sm:grid-cols-2">
            <SummaryItem label="Total Commercial Value" value={`£${formatNumber(summary.commercial)}`} />
            <SummaryItem label="Total Residential Value" value={`£${formatNumber(summary.residential)}`} />
            <SummaryItem
              label="Combined End Value"
              value={`£${formatNumber(combinedValue)}`}
              isPrimary
            />
            <SummaryItem label="Total Adopted Income" value={`£${formatNumber(summary.income)}`} />
          </dl>
        </div>
      </section>
    </div>
  )
}

function UnitCard({ unit, rowLabel, canRemove, marketGuidance, onUpdate, onRemove }) {
  const isCommercial = isCommercialUseType(unit.useType)
  const unitSummary = calculateCommercialUnit(unit)

  return (
    <article className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
      <div className="grid gap-5 border-b border-border/30 bg-muted/10 p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_2rem]">
        <FieldInput
          label="Unit ID / Name"
          ariaLabel={`${rowLabel} name`}
          value={unit.name}
          onChange={(value) => onUpdate('name', value)}
        />
        <LabeledSelect
          label="Use Type"
          ariaLabel={`${rowLabel} use type`}
          value={unit.useType}
          options={USE_TYPES}
          onValueChange={(value) => onUpdate('useType', value)}
        />
        <AppraisalField
          label="Size (sqft)"
          value={unit.sizeSqft}
          inputMode="decimal"
          aria-label={`${rowLabel} size`}
          onChange={(event) => onUpdate('sizeSqft', event.target.value)}
          className="bg-background font-medium"
        />
        <div className="flex items-end justify-end">
          {canRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove ${rowLabel}`}
              onClick={onRemove}
              className="bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground"
            >
              <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <div aria-hidden="true" className="h-8 w-8" />
          )}
        </div>
      </div>

      {!isCommercial ? (
        <div className="grid gap-5 border-b border-border/30 bg-muted/5 px-4 py-4 md:grid-cols-2">
          <SegmentedControl
            label="Operating Model"
            ariaLabel={`${rowLabel} operating model`}
            options={RESIDENTIAL_OPERATING_MODELS}
            value={unit.operatingModel}
            onChange={(value) => onUpdate('operatingModel', value)}
          />
          <LabeledSelect
            label="Valuation Basis"
            ariaLabel={`${rowLabel} valuation basis`}
            value={unit.valuationBasis}
            options={COMMERCIAL_VALUATION_BASES}
            onValueChange={(value) => onUpdate('valuationBasis', value)}
          />
        </div>
      ) : null}

      <div className="space-y-5 p-4">
        {isCommercial ? (
          <CommercialUnitFields
            unit={unit}
            unitSummary={unitSummary}
            rowLabel={rowLabel}
            marketGuidance={marketGuidance}
            onUpdate={onUpdate}
          />
        ) : (
          <ResidentialUnitFields
            unit={unit}
            unitSummary={unitSummary}
            rowLabel={rowLabel}
            marketGuidance={marketGuidance}
            onUpdate={onUpdate}
          />
        )}
      </div>
    </article>
  )
}

function CommercialUnitFields({ unit, unitSummary, rowLabel, marketGuidance, onUpdate }) {
  return (
    <div className="grid gap-5 md:grid-cols-3">
      <ReadOnlyValue label="Suggested £/sqft / year" value={suggestedCurrency(unit.suggestedSqftYear)} icon={Zap} />
      <AppraisalField
        label="Adopted £/sqft / year *"
        value={unit.adoptedSqftYear}
        prefix="£"
        inputMode="decimal"
        aria-label={`${rowLabel} adopted sqft year`}
        {...marketGuidance(unit.suggestedSqftYear, unit.adoptedSqftYear)}
        onChange={(event) => onUpdate('adoptedSqftYear', event.target.value)}
        className="bg-background font-bold"
      />
      <ReadOnlyValue label="Derived Annual Rent" value={`£${formatNumber(unitSummary.derivedAnnualRent)}`} />
      <AppraisalField
        label="OPEX % *"
        value={unit.opexPercent}
        suffix="%"
        inputMode="decimal"
        aria-label={`${rowLabel} opex`}
        onChange={(event) => onUpdate('opexPercent', event.target.value)}
        className="bg-background font-bold"
      />
      <ReadOnlyValue label="Net Operating Income" value={`£${formatNumber(unitSummary.netOperatingIncome)}`} />
      <AppraisalField
        label="Adopted Yield % *"
        value={unit.adoptedYield}
        suffix="%"
        inputMode="decimal"
        aria-label={`${rowLabel} adopted yield`}
        onChange={(event) => onUpdate('adoptedYield', event.target.value)}
        className="bg-background font-bold"
      />
      <ReadOnlyValue label="Resulting Unit Value" value={`£${formatNumber(unitSummary.value)}`} />
    </div>
  )
}

function ResidentialUnitFields({ unit, unitSummary, rowLabel, marketGuidance, onUpdate }) {
  return (
    <div className="grid gap-5 md:grid-cols-2">
      {unit.operatingModel === 'AST' ? (
        <>
          <ReadOnlyValue label="Suggested Monthly Rent" value={suggestedCurrency(unit.suggestedMonthlyRent)} icon={Zap} />
          <AppraisalField
            label="Adopted Monthly Rent *"
            value={unit.adoptedMonthlyRent}
            prefix="£"
            inputMode="decimal"
            aria-label={`${rowLabel} adopted monthly rent`}
            {...marketGuidance(unit.suggestedMonthlyRent, unit.adoptedMonthlyRent)}
            onChange={(event) => onUpdate('adoptedMonthlyRent', event.target.value)}
            className="bg-background font-bold"
          />
        </>
      ) : (
        <>
          <ReadOnlyValue label="Suggested ADR" value={suggestedCurrency(unit.suggestedAdr)} icon={Zap} />
          <AppraisalField
            label="Adopted ADR *"
            value={unit.adoptedAdr}
            prefix="£"
            inputMode="decimal"
            aria-label={`${rowLabel} adopted adr`}
            {...marketGuidance(unit.suggestedAdr, unit.adoptedAdr)}
            onChange={(event) => onUpdate('adoptedAdr', event.target.value)}
            className="bg-background font-bold"
          />
          <AppraisalField
            label="Occupancy % *"
            value={unit.occupancy}
            suffix="%"
            inputMode="decimal"
            aria-label={`${rowLabel} occupancy`}
            onChange={(event) => onUpdate('occupancy', event.target.value)}
            className="bg-background font-bold"
          />
          <AppraisalField
            label="OPEX % *"
            value={unit.opexPercent}
            suffix="%"
            inputMode="decimal"
            aria-label={`${rowLabel} opex`}
            onChange={(event) => onUpdate('opexPercent', event.target.value)}
            className="bg-background font-bold"
          />
          <ReadOnlyValue label="Net Monthly Income" value={`£${formatNumber(unitSummary.netMonthlyIncome)}`} />
          <ReadOnlyValue label="Net Annual Income" value={`£${formatNumber(unitSummary.netAnnualIncome)}`} />
        </>
      )}

      {unit.valuationBasis === 'Commercial' && unit.operatingModel === 'AST' ? (
        <AppraisalField
          label="OPEX % *"
          value={unit.opexPercent}
          suffix="%"
          inputMode="decimal"
          aria-label={`${rowLabel} opex`}
          onChange={(event) => onUpdate('opexPercent', event.target.value)}
          className="bg-background font-bold"
        />
      ) : null}

      {unit.valuationBasis === 'Commercial' ? (
        <>
          {unit.operatingModel === 'AST' ? (
            <ReadOnlyValue label="Net Operating Income" value={`£${formatNumber(unitSummary.netOperatingIncome)}`} />
          ) : null}
          <AppraisalField
            label="Adopted Yield % *"
            value={unit.adoptedYield}
            suffix="%"
            inputMode="decimal"
            aria-label={`${rowLabel} adopted yield`}
            onChange={(event) => onUpdate('adoptedYield', event.target.value)}
            className="bg-background font-bold"
          />
          <ReadOnlyValue label="Resulting Unit Value" value={`£${formatNumber(unitSummary.value)}`} />
        </>
      ) : null}

      {unit.valuationBasis === 'Comparable' ? (
        <>
          <ReadOnlyValue label="Suggested Value" value={suggestedCurrency(unit.suggestedValue)} icon={Zap} />
          <AppraisalField
            label="Adopted Value *"
            value={unit.adoptedValue}
            prefix="£"
            inputMode="decimal"
            aria-label={`${rowLabel} adopted value`}
            {...marketGuidance(unit.suggestedValue, unit.adoptedValue)}
            onChange={(event) => onUpdate('adoptedValue', event.target.value)}
            className="bg-background font-bold"
          />
        </>
      ) : null}

      {unit.valuationBasis === '£/sqft' ? (
        <>
          <ReadOnlyValue label="Suggested £/sqft" value={suggestedCurrency(unit.suggestedSqftValue)} icon={Zap} />
          <AppraisalField
            label="Adopted £/sqft *"
            value={unit.adoptedSqftValue}
            prefix="£"
            inputMode="decimal"
            aria-label={`${rowLabel} adopted sqft value`}
            {...marketGuidance(unit.suggestedSqftValue, unit.adoptedSqftValue)}
            onChange={(event) => onUpdate('adoptedSqftValue', event.target.value)}
            className="bg-background font-bold"
          />
          <ReadOnlyValue label="Calculated Unit Value" value={`£${formatNumber(unitSummary.value)}`} />
          <AppraisalField
            label="Adopted Value *"
            value={unit.adoptedValue}
            prefix="£"
            inputMode="decimal"
            aria-label={`${rowLabel} adopted value`}
            onChange={(event) => onUpdate('adoptedValue', event.target.value)}
            className="bg-background font-bold"
          />
        </>
      ) : null}
    </div>
  )
}

function FieldInput({ label, ariaLabel, value, onChange }) {
  const inputId = useId()

  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={inputId}
        className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </Label>
      <Input
        id={inputId}
        value={value}
        aria-label={ariaLabel}
        className="bg-background font-medium"
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function LabeledSelect({ label, ariaLabel, value, options, onValueChange }) {
  const selectId = useId()

  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={selectId}
        className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger id={selectId} className="w-full bg-background" aria-label={ariaLabel ?? label}>
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function SegmentedControl({ label, ariaLabel, options, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div
        className="flex rounded-lg border border-border/50 bg-muted/30 p-1"
        role="group"
        aria-label={ariaLabel ?? label}
      >
        {options.map((option) => (
          <Button
            key={option}
            type="button"
            variant="ghost"
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            className={cn(
              'h-auto flex-1 rounded-md border border-transparent py-1.5 text-xs font-bold text-muted-foreground shadow-none hover:bg-background hover:text-foreground',
              value === option && 'border-border/50 bg-background text-foreground shadow-sm hover:bg-background',
            )}
          >
            {option}
          </Button>
        ))}
      </div>
    </div>
  )
}

function ReadOnlyValue({ label, value, icon: Icon }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="flex min-h-9 items-center gap-2 rounded-lg border border-border/30 bg-muted/10 px-3 py-2.5 text-sm font-bold text-foreground">
        {Icon ? <Icon aria-hidden="true" className="h-3.5 w-3.5 text-primary" /> : null}
        {value}
      </p>
    </div>
  )
}

function suggestedCurrency(value) {
  return value ? `£${value}` : 'Unavailable'
}

function SummaryItem({ label, value, isPrimary = false }) {
  return (
    <div className={cn(isPrimary && 'border-t border-border/50 pt-4')}>
      <dt className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </dt>
      <dd className={cn('text-xl font-bold text-foreground', isPrimary && 'text-primary')}>
        {value}
      </dd>
    </div>
  )
}
